import {
	Card,
	Checkbox,
	ColorPicker,
	Divider,
	Empty,
	Flex,
	Form,
	Input,
	Modal,
	Select,
	TreeSelect,
	Typography
} from 'antd';
import React from 'react';
import * as C from 'src/pages/indicators/EditIndicatorPage/_components/ComponentsStore/styles';
import { storeComponents } from 'src/pages/indicators/EditIndicatorPage/storeComponents/storeComponents';
import RenderViewFromTemplate from 'src/components/RenderViewFromTemplate';
import { objectToAntdTree } from 'src/pages/indicators/EditIndicatorPage/helpers/objectToAntdTree';
import { IDashboardTree } from 'src/pages/indicators/EditIndicatorPage/types/dashboardTree';
import { IArgument, IStoreComponent } from 'src/pages/indicators/EditIndicatorPage/types/storeComponent';
import { AggregationColor } from 'antd/es/color-picker/color';
import antdIconsMap from 'src/common/mapping/antdIconsMap';
import IconAsText from 'src/components/IconAsText';
import { BaseOptionType } from 'antd/es/select';
import { DefaultOptionType } from 'antd/es/cascader';

interface IComponentsStoreProps {
	dashboardTree: IDashboardTree;
	onTemplateGenerated: (template: string) => void;
}

export default function ComponentsStore({
	onTemplateGenerated,
	dashboardTree,
}: IComponentsStoreProps) {
	const [ isModalOpen, setIsModalOpen, ] = React.useState<boolean>(false);
	const [ form, ] = Form.useForm();
	const [ selectedComponent, setSelectedComponent, ] =
        React.useState<IStoreComponent>();
	const [ selectedObserver, setSelectedObserver, ] = React.useState<string>('');
	const [ builtTemplate, setBuiltTemplate, ] = React.useState('');

	const [ search, setSearch, ] = React.useState<string>('');

	const [ filteredStoreComponents, setFilteredStoreComponents, ] =
        React.useState<IStoreComponent[]>(storeComponents);

	const onFormValuesChange = (_: any, allValues: any) => {
		const argumentsEntries = Object.entries(allValues);

		const fnArguments = argumentsEntries.map(([ key, value, ]) => {
			const originalArgument = selectedComponent?.template.arguments?.find(
				(argument) => argument.name === key
			);

			if (!!originalArgument && originalArgument.type === 'tree') {
				return {
					name: key,
					value: `{{${value}}}`,
				};
			}
			if(!!originalArgument && originalArgument.type === 'observer-select'){
				typeof value == 'string' && setSelectedObserver(value);
				return {
					name: key,
					value: `{{${value}.occurence.affectedRows.result | toJson}}`,
				};
			}
			if (value instanceof AggregationColor) {
				return {
					name: key,
					value: '#' + value.toHex(),
				};
			}
			return {
				name: key,
				value: value ? String(value) : '',
			};
		});

		const template = `${selectedComponent?.template.name}(${fnArguments
			.map((argument) => argument.value)
			.join(', ')})`;

		setBuiltTemplate(template);
	};

	React.useEffect(() => {
		setFilteredStoreComponents(
			storeComponents.filter(
				(item) =>
					item.title
						.toLowerCase()
						.trim()
						.includes(search.toLowerCase().trim()) ||
                    item?.description?.toLowerCase().trim().includes(search.toLowerCase().trim())
			)
		);
	}, [ search, ]);

	const content = filteredStoreComponents?.map((item, key) => (
		<Card
			key={key}
			bordered

			title={item.title}
			hoverable
			onClick={() => {
				setSelectedComponent(item);
				setIsModalOpen(true);
			}}
			style={{
				minWidth: 400,
				maxWidth: 'calc(25% - 20px)',
				minHeight: '310px',
				height: '100%',
				flex: 1,
				flexGrow: 1,
			}}
			cover={
				<C.PreviewContainer key={key}>
					<RenderViewFromTemplate
						templateProxy={item.exampleTemplate}
						viewProxy={item.exampleView}
					/>
				</C.PreviewContainer>
			}
		>
			<Card.Meta
				description={
					<Typography.Text title={item.description} ellipsis style={{ maxWidth: '100%', width: '100%', }}>
						{item.description}
					</Typography.Text>
				}
			/>
		</Card>
	));

	const generateFormItem = (argument: IArgument) => {
		switch (argument.type) {
			case 'boolean': {
				return (
					<Form.Item
						tooltip={argument.tooltip}
						label={argument.name}
						name={argument.name}
						valuePropName='checked'
					>
						<Checkbox />
					</Form.Item>
				);
			}
			case 'color': {
				return (
					<Form.Item
						tooltip={argument.tooltip}
						label={argument.name}
						name={argument.name}
					>
						<ColorPicker format='hex' />
					</Form.Item>
				);
			}
			case 'string': {
				return (
					<Form.Item
						tooltip={argument.tooltip}
						label={argument.name}
						name={argument.name}
					>
						<Input placeholder={`${argument.name}...`} />
					</Form.Item>
				);
			}
			case 'tree': {
				return (
					<Form.Item
						tooltip={argument.tooltip}
						label={argument.name}
						name={argument.name}
					>
						<TreeSelect
							style={{ width: '100%', }}
							treeData={objectToAntdTree(
								dashboardTree,
								'',
								argument?.acceptArray,
								'',
								argument?.restrictTreeType
							)}
						/>
					</Form.Item>
				);
			}
			case 'icon': {
				const options = Object.entries(antdIconsMap)?.map(
					([ index, ]) => {
						return {
							label: (
								<Flex align='center' gap={10}>
									<IconAsText
										icon={
											index as keyof typeof antdIconsMap
										}
										color='black'
									/>
									<Typography.Text>{index}</Typography.Text>
								</Flex>
							),
							value: index,
						};
					}
				);
				return (
					<Form.Item
						tooltip={argument.tooltip}
						label={argument.name}
						name={argument.name}
					>
						<Select showSearch options={options} />
					</Form.Item>
				);
			}
			case 'select': {
				return (
					<Form.Item
						tooltip={argument.tooltip}
						label={argument.name}
						name={argument.name}
					>
						<Select
							options={argument.selectOptions?.map((option) => {
								return {
									label: option.label,
									value: option.value,
								};
							})}
						/>
					</Form.Item>
				);
			}
			case 'observer-select': {
				return (
					<Form.Item
						tooltip={argument.tooltip}
						label={argument.name}
						name={argument.name}
					>
						<Select
							options={Object.keys(dashboardTree).map((key) => (
								{
									label: key,
									value: key,
								}
							))}
						/>
					</Form.Item>
				);
			}
			case 'observer-field': {
				return (
					<Form.Item
						tooltip={argument.tooltip}
						label={argument.name}
						name={argument.name}
					>
						<Select
							options={Object.keys(dashboardTree[selectedObserver]
								?.occurence
								?.affectedRows
								?.result
								?.at(0) ?? [])
								.map((key) => {
									if(key == 'id'){
										return null;
									}
									return (
										{
											label: key,
											value: key,
										}
									);
								}).filter(Boolean) as (BaseOptionType | DefaultOptionType)[]}
						/>
					</Form.Item>
				);
			}
		}
	};

	return (
		<C.MainContainer>
			<Flex
				justify='space-between'
				style={{ width: '100%', marginBottom: '-10px', }}
			>
				<Typography.Title level={5}>
                    Loja de Componentes
				</Typography.Title>
				<Input.Search
					value={search}
					onChange={(e) => {
						setSearch(e.target.value);
					}}
					style={{ maxWidth: 200, }}
					placeholder='Buscar'
				/>
			</Flex>
			<Divider style={{ margin: 0, }} />
			<C.CardsContainer>
				{!!content?.length && content}
				{!content?.length && (
					<Empty
						description={'Não há itens correspondentes'}
						style={{ width: '100%', }}
					/>
				)}
			</C.CardsContainer>
			<Modal
				title={'Opções de configuração'}
				open={isModalOpen}
				onOk={() => {
					onTemplateGenerated(builtTemplate);
					setIsModalOpen(false);
				}}
				onCancel={() => {
					setIsModalOpen(false);
				}}
				afterClose={() => {
					form.resetFields();
					setBuiltTemplate('');
				}}
				okText='Adicionar ao Dashboard'
				centered
				destroyOnHidden
			>
				{
					!!selectedComponent 
					&& !!selectedComponent.template.arguments?.length 
					&& selectedComponent
						.template
						.arguments
						?.map((argument, key) => (	
							<Form
								key={key}
								onValuesChange={onFormValuesChange}
								style={{ marginTop: 20, }}
								form={form}
							>
								{generateFormItem(argument)}
							</Form>
						)
						)}
				<Typography>Pré Visualização:</Typography>
				<C.ModalPreview>
					<RenderViewFromTemplate
						templateProxy={builtTemplate}
						viewProxy={dashboardTree}
					/>
				</C.ModalPreview>
			</Modal>
		</C.MainContainer>
	);
}
